import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type DrawCardContentDb = {
  cardId: string;
  content: string;
  goodsId?: string;
  version?: number;
}
const initial: DrawCardContentDb = {
  cardId: '',
  content: '',
  goodsId: undefined,
  version: undefined
};

export type DrawCardContentDbHashType = {
  [key in string]: DrawCardContentDb;
}

export type DrawCardContentAddPayload = {
  id?: string;
} & DrawCardContentDb;

export type DrawCardContentUpdatePayload = {
  id: string;
} & { [P in keyof DrawCardContentDb]?: DrawCardContentDb[P] };

export type DrawCardContentDbState = {
  drawCardContents: DrawCardContentDbHashType;
}

export const generateDrawCardContentId = (): string => generateId('dcc');
let initialState: DrawCardContentDbState = { drawCardContents: {} };
const key = 'drawCardContents';

const drawCardContentDbSlice = createSlice({
  name: 'drawCardContentDb',
  initialState,
  reducers: {
    initializeDrawCardContents(state: DrawCardContentDbState) {
      state.drawCardContents = {};
    },
    addDrawCardContent(state: DrawCardContentDbState, action: PayloadAction<DrawCardContentAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateDrawCardContentId();
      state.drawCardContents[id] = obj;
      saveData(key, id, obj);
    },
    updateDrawCardContent(state: DrawCardContentDbState, action: PayloadAction<DrawCardContentUpdatePayload>) {
      const { id, ...obj } = action.payload;
      const drawCardContent = state.drawCardContents[id];
      if (drawCardContent === undefined) {
        console.log(`*** DrawCardContent not found: ${id}`);
      } else {
        const newData = { ...state.drawCardContents[id], ...obj };
        state.drawCardContents[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteDrawCardContent(state: DrawCardContentDbState, action: PayloadAction<DrawCardContentUpdatePayload>) {
      const id = action.payload.id;
      delete state.drawCardContents[id];
      deleteData(key, id);
    },
    saveDrawCardContents(state: DrawCardContentDbState, action: PayloadAction<void>) {
      saveAllData(key, state.drawCardContents);
    },
    signUpDrawCardContents(state: DrawCardContentDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.drawCardContents, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: DrawCardContentDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.drawCardContents = updatedValues;
      if (changed) saveAllData(key, state.drawCardContents);
    })
  }
})

export const { initializeDrawCardContents, addDrawCardContent, updateDrawCardContent, deleteDrawCardContent, saveDrawCardContents, signUpDrawCardContents } = drawCardContentDbSlice.actions;
export default drawCardContentDbSlice.reducer;
